import React, { useState } from 'react';
import { Loader2, Sparkles, Wand2, ImagePlus, Check, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import api, { errText, mediaUrl } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import VoiceRecorder from '../../components/VoiceRecorder';
import BlogComposer from '../../components/BlogComposer';
import RichTextEditor from '../../components/RichTextEditor';

const TYPES = ['event', 'retreat', 'class'];
const MODES = ['in_person', 'online', 'hybrid'];
const LEVELS = ['all', 'beginner', 'intermediate', 'advanced'];

const toLocalInput = (iso) => {
  if (!iso) return '';
  try { const d = new Date(iso); const p = (n) => String(n).padStart(2, '0'); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`; } catch { return ''; }
};

const AdminAssistant = () => {
  const { toast } = useToast();
  const [text, setText] = useState('');
  const [mode, setMode] = useState('event');
  const [med, setMed] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [form, setForm] = useState(null);
  const [flyering, setFlyering] = useState(false);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [basedOn, setBasedOn] = useState(null);
  const [baseFull, setBaseFull] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const draft = async () => {
    if (text.trim().length < 5) return toast({ title: 'Describe the event first' });
    setDrafting(true); setCreated(null); setBasedOn(null); setBaseFull(null);
    try {
      const r = await api.post('/admin/assistant/draft-event', { text });
      const d = r.data.draft || {};
      let type = d.type || 'event';
      let mode = d.mode || 'in_person';
      if (type === 'online') { type = 'event'; mode = 'online'; }
      if (type === 'workshop') { type = 'class'; }
      if (!TYPES.includes(type)) { type = 'event'; }
      setForm({
        title: d.title || '', type, short_description: d.short_description || '',
        description: d.description || '', mode, location: d.location || '',
        country: d.country || '', start_datetime: toLocalInput(d.start_datetime), end_datetime: toLocalInput(d.end_datetime),
        duration_minutes: d.duration_minutes || '', category: d.category || '', level: d.level || 'all',
        tags: (d.tags || []).join(', '), pricing_type: d.pricing_type || 'free', price: d.price || '',
        capacity: d.capacity || '', featured_image_url: d.featured_image_url || '',
      });
      setBaseFull(r.data.base_full || null);
      setBasedOn(r.data.based_on_title || null);
      toast({ title: r.data.based_on_title ? `Copied from "${r.data.based_on_title}" — review & edit 🌸` : 'Draft ready — review & edit below 🌸' });
    } catch (e) { toast({ title: 'Drafting failed', description: errText(e) }); }
    finally { setDrafting(false); }
  };

  const makeFlyer = async () => {
    setFlyering(true);
    try {
      const r = await api.post('/admin/assistant/generate-flyer', { title: form.title, subtitle: form.short_description });
      set('featured_image_url', r.data.url);
      toast({ title: 'Flyer generated 🎨' });
    } catch (e) { toast({ title: 'Flyer failed', description: errText(e) }); }
    finally { setFlyering(false); }
  };

  const create = async () => {
    if (!form.title.trim()) return toast({ title: 'Title is required' });
    setCreating(true);
    try {
      const visible = {
        title: form.title, type: form.type, short_description: form.short_description, description: form.description,
        mode: form.mode, location: form.location || null, country: form.country || null,
        start_datetime: form.start_datetime || null, end_datetime: form.end_datetime || null,
        duration_minutes: form.duration_minutes ? parseInt(form.duration_minutes, 10) : null,
        category: form.category || null, level: form.level,
        tags: form.tags ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
        pricing_type: form.pricing_type, price: form.price ? parseFloat(form.price) : null,
        capacity: form.capacity ? parseInt(form.capacity, 10) : null,
        featured_image_url: form.featured_image_url || null,
      };
      // When duplicating, keep ALL of the original event's pricing plans, capacities, tickets,
      // add-ons, itinerary, etc. from base_full and layer the edited fields on top.
      const payload = baseFull
        ? { ...baseFull, ...visible, is_published: false }
        : { ...visible, is_published: false };
      const r = await api.post('/programs', payload);
      setCreated(r.data);
      toast({ title: 'Event created as draft ✅', description: 'Publish it from Programs when ready.' });
    } catch (e) { toast({ title: 'Create failed', description: errText(e) }); }
    finally { setCreating(false); }
  };

  const setM = (k, v) => setMed((f) => ({ ...f, [k]: v }));

  const draftMed = async () => {
    if (text.trim().length < 5) return toast({ title: 'Describe the meditation first' });
    setDrafting(true); setCreated(null);
    try {
      const r = await api.post('/admin/assistant/draft-meditation', { text });
      const d = r.data.draft || {};
      setMed({
        title: d.title || '', summary: d.summary || '', purposes: (d.purposes || []).join(', '),
        categories: (d.categories || []).join(', '), time_of_day: d.time_of_day || 'anytime',
        recommended_time: d.recommended_time || '', durations: (d.durations || []).join(', '),
        activity_level: d.activity_level || 'Gentle', overview: d.overview || '', preparation: d.preparation || '',
        stages: d.stages || '', after_meditation: d.after_meditation || '', key_principle: d.key_principle || '',
        source: d.source || 'Osho', hero_image: '',
      });
      toast({ title: 'Meditation draft ready — review & edit 🌸' });
    } catch (e) { toast({ title: 'Drafting failed', description: errText(e) }); }
    finally { setDrafting(false); }
  };

  const makeMedCover = async () => {
    setFlyering(true);
    try {
      const r = await api.post('/admin/assistant/generate-flyer', { title: med.title, subtitle: med.summary, style: 'serene meditative cover art, soft maroon and cream, calm sacred atmosphere, no text' });
      setM('hero_image', r.data.url);
      toast({ title: 'Cover generated 🎨' });
    } catch (e) { toast({ title: 'Cover failed', description: errText(e) }); }
    finally { setFlyering(false); }
  };

  const createMed = async () => {
    if (!med.title.trim()) return toast({ title: 'Title is required' });
    setCreating(true);
    try {
      const purposes = med.purposes ? med.purposes.split(',').map((t) => t.trim()).filter(Boolean) : [];
      const payload = {
        purpose: purposes[0] || 'Awareness & Witnessing', purposes,
        categories: med.categories ? med.categories.split(',').map((t) => t.trim()).filter(Boolean) : [],
        title: med.title, summary: med.summary, hero_image: med.hero_image || null,
        time_of_day: med.time_of_day, recommended_time: med.recommended_time || null,
        durations: med.durations ? med.durations.split(',').map((n) => parseInt(n.trim(), 10)).filter(Boolean) : [],
        activity_level: med.activity_level, overview: med.overview, preparation: med.preparation,
        stages: med.stages, after_meditation: med.after_meditation, key_principle: med.key_principle || null,
        source: med.source || null, is_free: false, is_published: false,
      };
      const r = await api.post('/admin/meditations', payload);
      setCreated({ title: r.data.title || med.title, kind: 'meditation' });
      toast({ title: 'Meditation created as draft ✅', description: 'Publish it from Meditations when ready.' });
    } catch (e) { toast({ title: 'Create failed', description: errText(e) }); }
    finally { setCreating(false); }
  };

  const MF = ({ label, k, as }) => (
    <label className="block">
      <span className="block text-xs uppercase tracking-widest text-mahogany/70 mb-1">{label}</span>
      {as === 'textarea'
        ? <textarea rows={4} value={med[k]} onChange={(e) => setM(k, e.target.value)} className="inp" />
        : <input value={med[k]} onChange={(e) => setM(k, e.target.value)} className="inp" />}
    </label>
  );

  const F = ({ label, k, type = 'text', as, opts }) => (
    <label className="block">
      <span className="block text-xs uppercase tracking-widest text-mahogany/70 mb-1">{label}</span>
      {as === 'textarea'
        ? <textarea rows={label === 'Description' ? 5 : 2} value={form[k]} onChange={(e) => set(k, e.target.value)} className="inp" />
        : as === 'select'
          ? <select value={form[k]} onChange={(e) => set(k, e.target.value)} className="inp">{opts.map((o) => <option key={o} value={o}>{o}</option>)}</select>
          : type === 'datetime-local'
            ? <input type="datetime-local" value={toLocalInput(form[k])} onChange={(e) => set(k, e.target.value ? new Date(e.target.value).toISOString() : '')} className="inp" />
            : <input type={type} value={form[k]} onChange={(e) => set(k, e.target.value)} className="inp" />}
    </label>
  );

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-assistant-page">
      <div>
        <h1 className="font-serif-display text-3xl text-burgundy flex items-center gap-2"><Sparkles className="w-6 h-6" /> AI Admin Assistant</h1>
        <p className="text-mahogany/70 text-sm">Describe an event, meditation or blog post in plain words — or dictate it by voice. The assistant drafts it for you to review, edit, illustrate and save as a draft.</p>
      </div>

      <div className="flex gap-2" data-testid="assistant-mode">
        {['event', 'meditation', 'blog'].map((mo) => (
          <button key={mo} onClick={() => { setMode(mo); setCreated(null); }} data-testid={`assistant-mode-${mo}`}
            className={`px-4 py-2 rounded-full text-sm border capitalize ${mode === mo ? 'bg-mahogany text-cream border-mahogany' : 'bg-white/60 text-burgundy border-rose/50'}`}>{mo}</button>
        ))}
      </div>

      {mode === 'blog' && (
        <BlogComposer
          createEndpoint="/admin/blog/posts"
          updateEndpoint={(id) => `/admin/blog/posts/${id}`}
          canPublish
          onSaved={() => toast({ title: 'Saved to Blog & News ✅' })}
        />
      )}

      {mode !== 'blog' && (
      <div className="rounded-2xl border border-rose/40 bg-white/70 p-5">
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} data-testid="assistant-input"
          placeholder={mode === 'event'
            ? 'New event, e.g. "A 3-day silent retreat at Osho Kaivalya Dham starting May 2, in-person, $250, 25 people…"  •  Or duplicate a past one: "Duplicate the Guru Purnima Celebration for July 2027, price $60, online"'
            : 'e.g. A 20-minute morning breathing meditation for anxiety, done sitting in a private space, 3 stages…'}
          className="inp" />
        {mode === 'event' && (
          <p className="text-xs text-mahogany/60 mt-2">Tip: mention a past event's name to copy everything from it — the AI keeps its details &amp; cover image and only changes what you ask.</p>
        )}
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <button onClick={mode === 'event' ? draft : draftMed} disabled={drafting} className="pill-btn" data-testid="assistant-draft-btn">
            {drafting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Draft with AI
          </button>
          <VoiceRecorder label="Dictate" testid="assistant-voice" onTranscript={(t) => setText((p) => (p ? p + ' ' : '') + t)} />
        </div>
      </div>
      )}

      {mode === 'meditation' && med && (
        <div className="rounded-2xl border border-rose/40 bg-white/70 p-5 space-y-4" data-testid="assistant-med-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <MF label="Title" k="title" />
            <MF label="Source" k="source" />
            <div className="sm:col-span-2"><MF label="Summary" k="summary" as="textarea" /></div>
            <MF label="Purposes (comma)" k="purposes" />
            <MF label="Categories (comma)" k="categories" />
            <MF label="Time of day" k="time_of_day" />
            <MF label="Recommended time" k="recommended_time" />
            <MF label="Durations (min, comma)" k="durations" />
            <MF label="Activity level" k="activity_level" />
            <div className="sm:col-span-2"><MF label="Overview" k="overview" as="textarea" /></div>
            <div className="sm:col-span-2"><MF label="Preparation" k="preparation" as="textarea" /></div>
            <div className="sm:col-span-2"><MF label="Stages" k="stages" as="textarea" /></div>
            <div className="sm:col-span-2"><MF label="After meditation" k="after_meditation" as="textarea" /></div>
            <div className="sm:col-span-2"><MF label="Key principle" k="key_principle" /></div>
          </div>
          <div className="flex items-center gap-4 border-t border-rose/30 pt-4">
            {med.hero_image
              ? <img src={mediaUrl(med.hero_image)} alt="cover" className="w-28 h-28 rounded-xl object-cover" data-testid="assistant-med-cover" />
              : <div className="w-28 h-28 rounded-xl bg-rose-soft/40 flex items-center justify-center text-mahogany/40"><ImagePlus className="w-6 h-6" /></div>}
            <button onClick={makeMedCover} disabled={flyering || !med.title} className="pill-btn-outline" data-testid="assistant-med-cover-btn">
              {flyering ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} {flyering ? 'Generating…' : 'Generate cover'}
            </button>
            <span className="text-xs text-mahogany/50">Premium · unpublished draft</span>
          </div>
          <div className="flex items-center justify-between border-t border-rose/30 pt-4">
            {created?.kind === 'meditation'
              ? <Link to="/dashboard/admin/meditations" className="text-green-700 flex items-center gap-1 text-sm"><Check className="w-4 h-4" /> Created “{created.title}” — view in Meditations <ExternalLink className="w-3.5 h-3.5" /></Link>
              : <span className="text-xs text-mahogany/50">Creates as a premium, unpublished meditation.</span>}
            <button onClick={createMed} disabled={creating} className="pill-btn" data-testid="assistant-med-create-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Create meditation (draft)
            </button>
          </div>
        </div>
      )}

      {mode === 'event' && form && (
        <div className="rounded-2xl border border-rose/40 bg-white/70 p-5 space-y-4" data-testid="assistant-draft-form">
          {basedOn && (
            <div className="flex items-center gap-2 text-sm text-mahogany bg-rose-soft/50 border border-rose/40 rounded-xl px-3 py-2" data-testid="assistant-based-on">
              <Sparkles className="w-4 h-4" /> Copied from <strong>“{basedOn}”</strong> — its details &amp; cover were carried over. Adjust anything below.
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            <F label="Title" k="title" />
            <F label="Type" k="type" as="select" opts={TYPES} />
            <div className="sm:col-span-2"><F label="Short description" k="short_description" as="textarea" /></div>
            <div className="sm:col-span-2">
              <label className="block text-xs uppercase tracking-widest text-mahogany/70 mb-1.5">Description</label>
              <RichTextEditor value={form.description} onChange={(html) => set('description', html)} />
            </div>
            <F label="Mode" k="mode" as="select" opts={MODES} />
            <F label="Level" k="level" as="select" opts={LEVELS} />
            <F label="Location" k="location" />
            <F label="Country" k="country" />
            <F label="Start" k="start_datetime" type="datetime-local" />
            <F label="End" k="end_datetime" type="datetime-local" />
            <F label="Duration (min)" k="duration_minutes" type="number" />
            <F label="Capacity" k="capacity" type="number" />
            <F label="Pricing" k="pricing_type" as="select" opts={['free', 'flat', 'per_day', 'tickets']} />
            <F label="Price (USD)" k="price" type="number" />
            <div className="sm:col-span-2"><F label="Tags (comma separated)" k="tags" /></div>
          </div>

          <div className="flex items-center gap-4 border-t border-rose/30 pt-4">
            {form.featured_image_url
              ? <img src={mediaUrl(form.featured_image_url)} alt="flyer" className="w-28 h-28 rounded-xl object-cover" data-testid="assistant-flyer-preview" />
              : <div className="w-28 h-28 rounded-xl bg-rose-soft/40 flex items-center justify-center text-mahogany/40"><ImagePlus className="w-6 h-6" /></div>}
            <button onClick={makeFlyer} disabled={flyering || !form.title} className="pill-btn-outline" data-testid="assistant-flyer-btn">
              {flyering ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} {flyering ? 'Generating…' : 'Generate flyer'}
            </button>
            <span className="text-xs text-mahogany/50">GPT Image 1 · ~0.2 credits</span>
          </div>

          <div className="flex items-center justify-between border-t border-rose/30 pt-4">
            {created
              ? <Link to="/dashboard/programs" className="text-green-700 flex items-center gap-1 text-sm"><Check className="w-4 h-4" /> Created “{created.title}” — view in Programs <ExternalLink className="w-3.5 h-3.5" /></Link>
              : <span className="text-xs text-mahogany/50">Creates as an unpublished draft you can review & publish.</span>}
            <button onClick={create} disabled={creating} className="pill-btn" data-testid="assistant-create-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Create event (draft)
            </button>
          </div>
        </div>
      )}
      <style>{`.inp{width:100%;background:#fff;border:1px solid rgba(191,131,143,.5);border-radius:.6rem;padding:.5rem .75rem;font-size:.9rem;color:#4A1721}`}</style>
    </div>
  );
};

export default AdminAssistant;
